import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Brain, Sparkles, AlertTriangle, Search, X, User } from "lucide-react";

export default function AIAnalyticsPage() {
  const [period, setPeriod] = useState("30");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  // Search employee
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);

  useEffect(() => {
    if (!searchQ || selectedEmp) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try { setSearchResults((await api.get(`/employees/search?q=${encodeURIComponent(searchQ)}`)).data); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [searchQ, selectedEmp]);

  const analyze = async () => {
    setLoading(true); setReport(null);
    try {
      const payload = { period_days: Number(period) };
      if (selectedEmp) payload.employee_id = selectedEmp.id;
      const r = await api.post("/ai/attendance-analysis", payload);
      setReport(r.data);
      if (r.data.has_critical_anomalies) {
        toast.warning("⚠️ Anomalie critiche rilevate · notifica email inviata all'admin");
      } else {
        toast.success("Analisi completata");
      }
    } catch { toast.error("Errore durante l'analisi"); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-4 md:p-12" data-testid="ai-analytics-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">AI Analytics</div>
        <h1 className="font-display text-4xl flex items-center gap-3"><Brain className="w-8 h-8 text-[#FF7A29]"/> Analisi Presenze</h1>
        <p className="text-[#555] mt-1 text-sm">Powered by GPT-5.2 · Analisi su azienda completa o singolo dipendente · Notifica email automatica per anomalie critiche</p>
      </div>

      <div className="card-smart p-6 mb-6">
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div>
            <Label>Periodo</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                <SelectItem value="90">Ultimi 90 giorni</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Dipendente specifico (opzionale)</Label>
            {selectedEmp ? (
              <div className="flex items-center gap-2 mt-1 bg-[#FFF0E3] border border-[#FF7A29] rounded-xl px-4 py-2">
                <User className="w-4 h-4 text-[#FF7A29]"/>
                <span className="font-semibold flex-1">{selectedEmp.name} {selectedEmp.lastname}</span>
                <span className="text-xs text-[#757575]">{selectedEmp.email}</span>
                <button onClick={()=>{setSelectedEmp(null); setSearchQ("");}} data-testid="clear-employee" className="hover:bg-white rounded-full p-1"><X className="w-3.5 h-3.5"/></button>
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#757575]"/>
                  <Input data-testid="ai-emp-search" className="rounded-xl pl-10" value={searchQ} onChange={(e)=>setSearchQ(e.target.value)} placeholder="Cerca per nome, cognome, email o reparto…"/>
                </div>
                {searchResults.length > 0 && (
                  <div className="absolute z-10 bg-white border border-[#EDE5D8] rounded-xl mt-1 max-w-md shadow-lg divide-y divide-[#EDE5D8] max-h-60 overflow-auto">
                    {searchResults.map((e) => (
                      <button key={e.id} type="button" onClick={()=>{setSelectedEmp(e); setSearchResults([]); setSearchQ("");}} data-testid={`ai-emp-${e.id}`} className="w-full text-left px-4 py-2 hover:bg-[#FFF0E3]">
                        <div className="text-sm font-semibold">{e.name} {e.lastname}</div>
                        <div className="text-xs text-[#757575]">{e.email}{e.job && ` · ${e.job}`}</div>
                      </button>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-[#757575] mt-1">Lascia vuoto per analisi complessiva dell'azienda</div>
              </>
            )}
          </div>
        </div>
        <Button onClick={analyze} disabled={loading} data-testid="analyze-btn" className="mt-4 rounded-full bg-[#FF7A29] hover:bg-[#E85D00] btn-glow">
          <Sparkles className="w-4 h-4 mr-2"/> {loading ? "Analisi in corso…" : selectedEmp ? `Analizza ${selectedEmp.name} ${selectedEmp.lastname}` : "Analizza azienda"}
        </Button>
      </div>

      {report && (
        <div className="space-y-6" data-testid="ai-report">
          {report.has_critical_anomalies && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-2xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0"/>
              <div>
                <div className="font-semibold text-red-800">Anomalie critiche rilevate</div>
                <div className="text-xs text-red-700 mt-1">L'AI ha identificato pattern anomali. Una notifica email è stata inviata automaticamente all'admin dell'azienda.</div>
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="card-smart p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#757575]">Record totali</div>
              <div className="font-display text-4xl mt-1">{report.total_records}</div>
            </div>
            <div className="card-smart p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#757575]">Timbrature non valide</div>
              <div className="font-display text-4xl mt-1 text-amber-600">{report.invalid_records}</div>
            </div>
            <div className="card-smart p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#757575]">Dipendenti analizzati</div>
              <div className="font-display text-4xl mt-1">{Object.keys(report.per_user || {}).length}</div>
            </div>
          </div>
          <div className="card-smart p-6">
            <h3 className="font-display text-xl mb-4 flex items-center gap-2"><Brain className="w-5 h-5 text-[#FF7A29]"/> Report AI</h3>
            <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans text-[#333]">{report.ai_report}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
